import { makeStyles } from '@map/theme';
import { alpha, ButtonBase, ButtonBaseProps } from '@mui/material';
import { type ElementType, forwardRef, type HTMLAttributes } from 'react';

type StyleProps = { flat?: boolean };
const useStyles = makeStyles<StyleProps>('Card')(
  ({ borderRadius, palette, spacing, shadow, transitions }, { flat }) => ({
    root: {
      // standard Card properties
      display: 'flex',
      flexDirection: 'column',
      padding: spacing(4),
      backgroundColor: palette.background.paper,
      borderRadius: borderRadius('l'),
      position: 'relative',
      overflow: 'hidden',
      ...(!flat && { boxShadow: shadow('s') }),
      // end standard Card properties
      ...(customEnv.hrdf && {
        boxShadow: `0 0 0 1px ${palette.neutrals[400]} inset`,
        borderRadius: 8,
      }),
      borderColor: 'transparent',
      '--card-border-width': '2px',
      '--card-border-style': 'solid',
      '--card-hover-overlay-color': alpha(palette.neutrals[900], 0.15),
      '> *': {
        isolation: 'isolate',
      },
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      },
      '&::before': {
        transition: transitions.create('background-color', { duration: '0.1s' }),
        backgroundColor: 'transparent',
      },
      '&::after': {
        transition: transitions.create('border-color', { duration: '0.1s' }),
        borderRadius: 'inherit',
        pointerEvents: 'none',
        border: 'var(--card-border-width) var(--card-border-style) transparent',
        borderColor: 'inherit',
      },
    },
    clickable: {
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      '&:hover, &:focus-visible': {
        '&::before': {
          backgroundColor: 'var(--card-hover-overlay-color)',
        },
      },
    },
    selectable: {
      borderWidth: 2,
    },
    selected: {
      backgroundColor: palette.primary[100],
      borderColor: palette.primary.main,
    },
  }),
);

type BaseComponentProps = Omit<HTMLAttributes<HTMLElement>, 'id' | 'title' | 'ref'>;

export type Props = BaseComponentProps & {
  className?: string;
  selected?: boolean | null;
  disabled?: boolean | null;
  flat?: boolean;
  component?: ElementType;
};

const Card: FFC<Props, HTMLDivElement> = (props, ref) => {
  const { component = 'div', className, selected, flat, children, ...restProps } = props;
  const { classes, cx } = useStyles({ flat });

  const clickable = !!restProps.onClick;
  let Component = component;
  if (clickable) {
    Component = ButtonBase;
    const buttonBaseProps = restProps as unknown as ButtonBaseProps;
    buttonBaseProps.disableRipple = true;
    // MUI's types are nasty
    (buttonBaseProps as any).component = component;
  }

  return (
    <Component
      {...restProps}
      ref={ref}
      className={cx(
        classes.root,
        clickable && classes.clickable,
        typeof selected === 'boolean' && classes.selectable,
        selected && classes.selected,
        className,
      )}
    >
      {children}
    </Component>
  );
};

export default forwardRef(Card);
